import { format, isValid as isDateFnsValid, parse, parseISO } from "date-fns";
import { normalizarTexto } from "../utils";

const FORMATO_DATA_ISO = "yyyy-MM-dd";
const FORMATO_DATA_BR = "dd/MM/yyyy";
const FORMATO_DATA_HORA_BR = "dd/MM/yyyy HH:mm";
const FORMATO_HORA_BR = "HH:mm";

const NOMES_MESES = ["", "Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"] as const;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const BR_DATE_RE = /^\d{2}\/\d{2}\/\d{4}$/;
const ISO_DATE_TIME_WITHOUT_OFFSET_RE = /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;
const DATE_TIME_OFFSET_RE = /(?:Z|[+-]\d{2}:?\d{2})$/i;
const BACKEND_GMT_DATE_TIME_RE =
	/^(?:[a-z]{3},\s*)?\d{1,2}\s+[a-z]{3}\s+\d{4}\s+\d{2}:\d{2}(?::\d{2})?\s+(?:gmt|utc)$/i;

export type FormatarCompetenciaOptions<TFallback extends string, TReferencia extends string = "vencimento"> = {
	readonly competencia: string | null | undefined;
	readonly dataReferencia: string | null | undefined;
	readonly fallback: TFallback;
	readonly valorReferencia?: TReferencia;
};

// ------------------------------------------------- Helpers privados -----------------------------------
function isDataValida(date: Date): boolean {
	return isDateFnsValid(date);
}

function parseDataEstrita(value: unknown, pattern: string, regex: RegExp): Date | null {
	if (typeof value !== "string" || !regex.test(value)) return null;

	const parsed = parse(value, pattern, new Date(0));
	if (!isDataValida(parsed)) return null;

	return format(parsed, pattern) === value ? parsed : null;
}

function parseDataISO(value: unknown): Date | null {
	return parseDataEstrita(value, FORMATO_DATA_ISO, ISO_DATE_RE);
}

function parseDataBR(value: unknown): Date | null {
	return parseDataEstrita(value, FORMATO_DATA_BR, BR_DATE_RE);
}

function extrairDataISO(value: unknown): string | null {
	if (isDataISOValida(value)) return value;
	if (typeof value !== "string") return null;

	const candidate = value.trim().slice(0, 10);
	return isDataISOValida(candidate) ? candidate : null;
}

function normalizarTextoDataHoraUTC(value: string): string | null {
	const raw = value.trim();
	if (!raw || ISO_DATE_RE.test(raw)) return null;

	if (DATE_TIME_OFFSET_RE.test(raw)) return raw.replace(" ", "T");
	if (ISO_DATE_TIME_WITHOUT_OFFSET_RE.test(raw)) return `${raw.replace(" ", "T")}Z`;

	return null;
}

function parseDataHoraBackendGMTComoLocal(value: string): Date | null {
	const raw = value.trim();
	if (!BACKEND_GMT_DATE_TIME_RE.test(raw)) return null;

	// Algumas respostas chegam como texto GMT, mas com o horário local já embutido.
	const textoSemDiaSemana = raw
		.replace(/^[a-z]{3},\s*/i, "")
		.replace(/\s+(gmt|utc)$/i, (suffix) => suffix.toUpperCase());
	const sufixo = textoSemDiaSemana.endsWith(" UTC") ? "UTC" : "GMT";
	const possuiSegundos = /\d{2}:\d{2}:\d{2}\s+(?:GMT|UTC)$/i.test(textoSemDiaSemana);
	const pattern = `d MMM yyyy HH:mm${possuiSegundos ? ":ss" : ""} '${sufixo}'`;
	const parsed = parse(textoSemDiaSemana, pattern, new Date(0));

	return isDataValida(parsed) ? parsed : null;
}

// ------------------------------------------------- Date-only ----------------------------------------------------
export function isDataISOValida(value: unknown): value is string {
	return parseDataISO(value) !== null;
}

export function formatarDataBRParaISO(value: unknown): string | null {
	const parsed = parseDataBR(value);
	return parsed ? format(parsed, FORMATO_DATA_ISO) : null;
}

export function formatarDataISOParaBR(value: unknown): string | null {
	const iso = extrairDataISO(value);
	if (!iso) return null;

	const parsed = parseDataISO(iso);
	return parsed ? format(parsed, FORMATO_DATA_BR) : null;
}

export function normalizarDataParaISO(value: unknown): string | null {
	const iso = extrairDataISO(value);
	if (iso) return iso;

	const isoFromBR = formatarDataBRParaISO(value);
	if (isoFromBR) return isoFromBR;

	if (typeof value !== "string") return null;

	const raw = value.trim();
	if (!raw) return null;
	if (ISO_DATE_RE.test(raw.slice(0, 10)) || BR_DATE_RE.test(raw)) return null;

	const backendGMTDate = parseDataHoraBackendGMTComoLocal(raw);
	if (backendGMTDate) return format(backendGMTDate, FORMATO_DATA_ISO);

	const parsedISO = parseISO(raw);
	if (isDataValida(parsedISO)) return parsedISO.toISOString().slice(0, 10);

	const parsed = new Date(raw);
	return isDataValida(parsed) ? parsed.toISOString().slice(0, 10) : null;
}

export function normalizarTextoDataParaISO(value: unknown): string | null {
	if (typeof value !== "string") return null;

	const raw = value.trim();
	if (!raw) return raw;

	return normalizarDataParaISO(raw) ?? raw;
}

// ------------------------------------------------- Date-time ------------------------------------------------
export function parseDataHoraUTC(value: unknown): Date | null {
	if (value instanceof Date) return isDataValida(value) ? value : null;
	if (typeof value !== "string") return null;

	const backendGMTDate = parseDataHoraBackendGMTComoLocal(value);
	if (backendGMTDate) return backendGMTDate;

	const normalized = normalizarTextoDataHoraUTC(value);
	if (!normalized) return null;

	const parsed = parseISO(normalized);
	return isDataValida(parsed) ? parsed : null;
}

export function formatarDataHoraUTCParaLocalBR(value: unknown): string | null {
	const date = parseDataHoraUTC(value);
	return date ? format(date, FORMATO_DATA_HORA_BR) : null;
}

function parseDataHoraLocal(value: unknown): Date | null {
	if (value instanceof Date) return isDataValida(value) ? value : null;
	if (typeof value !== "string") return null;

	const backendGMTDate = parseDataHoraBackendGMTComoLocal(value);
	if (backendGMTDate) return backendGMTDate;

	const raw = value.trim();
	if (!raw || ISO_DATE_RE.test(raw)) return null;

	const normalizado = raw.replace(" ", "T");

	if (ISO_DATE_TIME_WITHOUT_OFFSET_RE.test(raw) || DATE_TIME_OFFSET_RE.test(raw)) {
		const parsed = parseISO(normalizado);
		return isDataValida(parsed) ? parsed : null;
	}

	return null;
}

export function formatarDataHoraLocalBR(value: unknown): string | null {
	const date = parseDataHoraLocal(value);
	return date ? format(date, FORMATO_DATA_HORA_BR) : null;
}

export function formatarHoraLocalBR(value: unknown): string | null {
	const date = parseDataHoraLocal(value);
	return date ? format(date, FORMATO_HORA_BR) : null;
}

/**
 * Normaliza competência para formulário.
 *
 * Compara `competencia` com `dataReferencia` usando apenas mês/ano.
 * Se forem do mesmo mês/ano, retorna `valorReferencia` em `mesCompetencia`;
 * caso contrário, retorna `fallback`.
 *
 * Também retorna `competenciaDate` no formato `MM/YYYY`.
 *
 * Aceita:
 * - "2024-04-01"
 * - "2024-04"
 * - "04/2024"
 * - "30/04/2024"
 *
 * @example
 * formatarCompetencia({
 *   competencia: "2024-04-01",
 *   dataReferencia: "2024-04-30",
 *   fallback: "livre",
 * })
 * // { mesCompetencia: "vencimento", competenciaDate: "04/2024" }
 *
 * @example
 * formatarCompetencia({
 *   competencia: "2024-04-01",
 *   dataReferencia: "2024-04-30",
 *   fallback: "livre",
 *   valorReferencia: "dataVencimento",
 * })
 * // { mesCompetencia: "dataVencimento", competenciaDate: "04/2024" }
 */
export function formatarCompetencia<TFallback extends string, TReferencia extends string = "vencimento">({
	competencia,
	dataReferencia,
	fallback,
	valorReferencia = "vencimento" as TReferencia,
}: Readonly<FormatarCompetenciaOptions<TFallback, TReferencia>>): {
	mesCompetencia: TReferencia | TFallback;
	competenciaDate: string;
} {
	const competenciaTexto = normalizarTexto(competencia);
	const referenciaTexto = normalizarTexto(dataReferencia);

	const normalizarAnoMes = (value: string): string => {
		if (/^\d{4}-\d{2}/.test(value)) return value.slice(0, 7);

		if (/^\d{2}\/\d{4}$/.test(value)) {
			const [mes, ano] = value.split("/");

			return `${ano}-${mes}`;
		}

		if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
			const [, mes, ano] = value.split("/");

			return `${ano}-${mes}`;
		}

		return "";
	};

	const competenciaAnoMes = normalizarAnoMes(competenciaTexto);
	const referenciaAnoMes = normalizarAnoMes(referenciaTexto);

	let competenciaDate = competenciaTexto;

	if (competenciaAnoMes) {
		const [ano, mes] = competenciaAnoMes.split("-");

		competenciaDate = `${mes}/${ano}`;
	}

	return {
		mesCompetencia:
			competenciaAnoMes && referenciaAnoMes && competenciaAnoMes === referenciaAnoMes
				? valorReferencia
				: fallback,
		competenciaDate,
	};
}

/**
 * Converte uma competência `YYYY/MM` em rótulo curto (ex.: "2026/07" → "Jul/26").
 */
export function formatMesAnoCurto(competencia: string): string {
	const [ano, mes] = (competencia ?? "").split("/");
	const indice = Number.parseInt(mes ?? "", 10);
	const nome = NOMES_MESES[indice] ?? mes ?? "";
	return `${nome}/${(ano ?? "").slice(2)}`;
}
